import type { ReceivedOffer, SentOffer } from "@/app/hooks/use-app-state";
import type { CartItem } from "@/app/components/cart-page";

export const createOfferConversation = (offer: ReceivedOffer) => ({
  id: `buyer-offer-${offer.id}`,
  name: offer.buyerName,
  lastMessage: "Nova oferta recebida",
  timestamp: offer.createdAt,
  unreadCount: 1,
  type: "seller" as const,
  adTitle: offer.targetAd.title,
});

export const createSentOfferConversation = (offer: SentOffer) => ({
  id: `seller-offer-${offer.id}`,
  name: offer.sellerName,
  lastMessage: "Oferta enviada",
  timestamp: offer.createdAt,
  unreadCount: 0,
  type: "seller" as const,
  adTitle: offer.targetAd.title,
});

export const createOfferChatMessage = (offer: ReceivedOffer, conversationId: string) => ({
  id: `msg-received-${offer.id}`,
  conversationId,
  text: offer.message || "Gostaria de fazer uma oferta pelo seu produto.",
  sender: "other" as const,
  timestamp: offer.createdAt,
  offerData: {
    offerId: offer.id,
    offerType: offer.offerType,
    totalOfferValue: offer.totalOfferValue,
    targetAdTitle: offer.targetAd.title,
    targetAdId: offer.targetAd.id,
    status: "pending" as const,
  },
});

export const createSentOfferChatMessage = (offer: SentOffer, conversationId: string) => ({
  id: `msg-sent-${offer.id}`,
  conversationId,
  text: offer.message || "Gostaria de fazer uma oferta pelo seu produto.",
  sender: "user" as const,
  timestamp: offer.createdAt,
  offerData: {
    offerId: offer.id,
    offerType: offer.offerType,
    totalOfferValue: offer.totalOfferValue,
    targetAdTitle: offer.targetAd.title,
    targetAdId: offer.targetAd.id,
    status: offer.status === "accepted" ? "accepted" as const : "pending" as const,
  },
});

export const getSellerInitials = (sellerName: string): string => {
  return sellerName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

export const formatOfferCurrency = (value: number): string => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

/** Item that can be part of a thread (has id, optional parentOfferId, createdAt). */
type Threadable = { id: string; parentOfferId?: string; createdAt: Date };

/**
 * Orders offers so each counter (child) appears nested under its parent:
 * roots first (by createdAt desc), then each root's children immediately after it.
 */
export function orderOffersWithNested<T extends Threadable>(offers: T[]): T[] {
  const roots = offers.filter((o) => !o.parentOfferId).sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  const childrenByParent = new Map<string, T[]>();
  for (const o of offers) {
    if (o.parentOfferId) {
      const arr = childrenByParent.get(o.parentOfferId) ?? [];
      arr.push(o);
      childrenByParent.set(o.parentOfferId, arr);
    }
  }
  for (const arr of childrenByParent.values()) {
    arr.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }
  const result: T[] = [];
  const added = new Set<string>();
  for (const root of roots) {
    if (added.has(root.id)) continue;
    result.push(root);
    added.add(root.id);
    for (const c of childrenByParent.get(root.id) ?? []) {
      result.push(c);
      added.add(c.id);
    }
  }
  for (const o of offers) {
    if (!added.has(o.id)) result.push(o);
  }
  return result;
}

export type ThreadViewItem<T> =
  | { type: "single"; offer: T }
  | { type: "thread"; root: T | null; currentOffer: T };

/**
 * Groups offers into threads. Returns one item per "row" to render:
 * - "single": root with no children → render one card.
 * - "thread": root with children (or orphan counter) → one block (collapsible root + current offer card).
 * Orphan = offer with parentOfferId but parent not in list; still shown as thread (collapsible fetches parent by id).
 * Order: by most recent activity, desc.
 */
export function getThreadViewItems<T extends Threadable>(offers: T[]): ThreadViewItem<T>[] {
  const rootIds = new Set(offers.filter((o) => !o.parentOfferId).map((o) => o.id));
  const roots = offers.filter((o) => !o.parentOfferId).sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  const childrenByParent = new Map<string, T[]>();
  for (const o of offers) {
    if (o.parentOfferId) {
      const arr = childrenByParent.get(o.parentOfferId) ?? [];
      arr.push(o);
      childrenByParent.set(o.parentOfferId, arr);
    }
  }
  for (const arr of childrenByParent.values()) {
    arr.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }
  const items: ThreadViewItem<T>[] = [];
  const addedOfferIds = new Set<string>();

  for (const root of roots) {
    const children = childrenByParent.get(root.id) ?? [];
    if (children.length === 0) {
      items.push({ type: "single", offer: root });
      addedOfferIds.add(root.id);
    } else {
      const latestChild = children[0];
      items.push({ type: "thread", root, currentOffer: latestChild });
      addedOfferIds.add(root.id);
      addedOfferIds.add(latestChild.id);
    }
  }

  // Orphans: offers with parentOfferId not yet in any thread (e.g. parent not in this list)
  for (const o of offers) {
    if (o.parentOfferId && !addedOfferIds.has(o.id)) {
      const root = offers.find((r) => r.id === o.parentOfferId) ?? null;
      items.push({ type: "thread", root, currentOffer: o });
      addedOfferIds.add(o.id);
    }
  }

  items.sort((a, b) => {
    const dateA = a.type === "single" ? a.offer.createdAt.getTime() : a.currentOffer.createdAt.getTime();
    const dateB = b.type === "single" ? b.offer.createdAt.getTime() : b.currentOffer.createdAt.getTime();
    return dateB - dateA;
  });
  return items;
}

/**
 * Converts an accepted offer to a cart item.
 * Uses cashAmount (what the buyer actually pays) for the cart total, not totalOfferValue
 * (which includes the value of traded products and is only for display on the offer details).
 */
export const offerToCartItem = (offer: SentOffer): CartItem => ({
  ad: offer.targetAd,
  quantity: 1,
  offerId: offer.id,
  isOfferItem: true,
  offerPrice: offer.cashAmount,
  offerExpiration: offer.expiresAt,
  offerTradeInAds: offer.tradeInAds,
});